import { logHttp } from 'webPosApp/api';

export enum LogLevel {
    Trace = 0,
    Debug = 1,
    Information = 2,
    Warning = 3,
    Error = 4,
    Critical = 5,
    None = 6,
}

export class LogEvent {
    eventId: number;
    eventName: string | null;

    constructor(eventId: number, eventName: string | null = null) {
        this.eventId = eventId;
        this.eventName = eventName;
    }
}

export class LogEntry {
    event: LogEvent | null;

    level: LogLevel;
    message: string;

    parameters: any[] = [];

    constructor(
        level: LogLevel,
        message: string,
        event: LogEvent | null = null,
        ...parameters: any[]
    ) {
        this.event = event;
        this.level = level;
        this.message = message;
        this.parameters = parameters;
    }

    get eventId(): number | null {
        return this.event ? this.event.eventId : null;
    }

    get eventName(): string | null {
        return this.event ? this.event.eventName : null;
    }
}

export class LoggerOptions {
    debug: boolean = false;
    minLevel?: LogLevel;
}

export class Logger {
    options: LoggerOptions;

    constructor(options: LoggerOptions = new LoggerOptions()) {
        this.options = options;
    }

    log(
        level: LogLevel,
        message: string,
        event: LogEvent | null = null,
        data: any = null
    ) {
        return this.send(level, message, event, data);
    }

    logDebug(message: string, event: LogEvent | null = null, data: any = null) {
        return this.send(LogLevel.Debug, message, event, data);
    }

    logError(message: string, event: LogEvent | null = null, data: any = null) {
        console.error(message, event, data);

        return this.send(LogLevel.Error, message, event, data);
    }

    logInformation(
        message: string,
        event: LogEvent | null = null,
        data: any = null
    ) {
        return this.send(LogLevel.Information, message, event, data);
    }

    private send(
        level: LogLevel,
        message: string,
        event: LogEvent | null = null,
        ...parameters: any[]
    ) {
        const logEntry = new LogEntry(
            level,
            `${message} {dataJson}`,
            event,
            ...parameters
        );

        if (this.options.minLevel && this.options.minLevel > level) {
            this.options.debug &&
                console.warn(
                    `Skipping logEntry, because minLevel is ${this.options.minLevel}`,
                    logEntry,
                    this.options
                );
            return Promise.resolve();
        }

        this.options.debug && console.log(`Sending log message.`, logEntry);

        return logHttp.post('/api/logging/log', logEntry).then(
            (_) => {
                this.options.debug &&
                    console.log(`Successfuly sent log message.`, logEntry);
            },
            (error) => {
                this.options.debug &&
                    console.error(
                        `Failed to send log message.`,
                        logEntry,
                        error
                    );
            }
        );
    }
}

export const logger = new Logger({
    debug: false,

    minLevel: LogLevel.Information,
});

export class LogEvents {
    static WizardStepChanged: LogEvent = new LogEvent(98, 'WizardStepChanged');

    static UnhandledScriptError: LogEvent = new LogEvent(
        300,
        'UnhandledScriptError'
    );
}
